// @ts-nocheck
import { useEffect, useRef } from "react";
import { useCallStore, ACTIVE_CALL_STATES } from "@/router/zustand/callStore";
import { useTranscriptStore } from "@/router/zustand/transcriptStore";
import { createTranscriptionService } from "@/features/cti2/transcription/transcriptionService";
import { createCustomerNativeTranscriptionService } from "@/features/cti2/transcription/customerNativeTranscriptionService";
import { createTranscriptBatcher } from "@/features/cti2/transcription/transcriptApi";
import {
  getTranscriptionLang,
  isSpeechRecognitionAudioTrackSupported,
  blockCustomerAudioTrack,
} from "@/features/cti2/transcription/speechRecognition";
import { isWebrtcProfileActive } from "@/utilities/mobileCallEvents";
import { postApiCall } from "@/service/httpCall";
import { API_CALL_TRANSCRIPTS } from "@/service/url";
import {
  CUSTOMER_TRANSCRIPTION_ENABLED,
  CUSTOMER_NATIVE_TRANSCRIPTION_ENABLED,
} from "@/utilities/constant";

function resolveCallSid() {
  const s = useCallStore.getState();
  const meta = s.activeCallMeta || {};
  return meta.callSid || meta.call_sid || s.channelId || "";
}

function resolveTraceId() {
  const meta = useCallStore.getState().activeCallMeta || {};
  return meta.trace_id || meta.traceId || "";
}

export function useCallTranscription() {
  const callState = useCallStore((s) => s.callState);
  const supported = useTranscriptStore((s) => s.supported);
  const enabled = useTranscriptStore((s) => s.enabled);
  const customerLang = useTranscriptStore((s) => s.customerLang);
  const agentLang = useTranscriptStore((s) => s.agentLang);
  const agentMuted = useTranscriptStore((s) => s.agentMuted);

  const serviceRef = useRef(null);
  const batcherRef = useRef(null);
  const callSidRef = useRef("");

  const customerServiceRef = useRef(null);
  const customerBatcherRef = useRef(null);
  const customerCallSidRef = useRef("");

  const shouldRun =
    supported && enabled && !agentMuted && callState !== 'hold' &&
    isWebrtcProfileActive() && ACTIVE_CALL_STATES.includes(callState);
  const customerShouldRun =
    CUSTOMER_TRANSCRIPTION_ENABLED &&
    CUSTOMER_NATIVE_TRANSCRIPTION_ENABLED &&
    isSpeechRecognitionAudioTrackSupported() &&
    enabled &&
    isWebrtcProfileActive() &&
    ACTIVE_CALL_STATES.includes(callState);

  useEffect(() => {
    if (!shouldRun) return undefined;
    callSidRef.current = resolveCallSid();
    batcherRef.current = createTranscriptBatcher({
      post: (payload) => postApiCall(API_CALL_TRANSCRIPTS, payload),
      getMeta: () => ({
        callSid: callSidRef.current || resolveCallSid(),
        traceId: resolveTraceId(),
        provider: "browser_speech",
        language: useTranscriptStore.getState().agentLang || getTranscriptionLang(),
      }),
    });
    serviceRef.current = createTranscriptionService({
      lang: agentLang || getTranscriptionLang(),
      shouldRestart: () => {
        const cs = useCallStore.getState().callState;
        const ts = useTranscriptStore.getState();
        return (
          ts.supported && ts.enabled && !ts.agentMuted && cs !== 'hold' &&
          ts.agentLang === agentLang &&
          isWebrtcProfileActive() && ACTIVE_CALL_STATES.includes(cs)
        );
      },
      onInterim: (t) => {
        if (useTranscriptStore.getState().agentMuted) return;
        useTranscriptStore.getState().setInterim(t);
      },
      onFinal: (t) => {
        if (useTranscriptStore.getState().agentMuted) return;
        useTranscriptStore.getState().addFinalLine(t);
        batcherRef.current?.add({
          speaker: "Agent", source: "microphone", text: t,
          timestamp: new Date().toISOString(), isFinal: true,
        });
      },
      onError: (e) => useTranscriptStore.getState().setError(e),
      onStatus: (st) => useTranscriptStore.getState().setStatus(st),
    });
    serviceRef.current.start();
    return () => {
      if (serviceRef.current) { serviceRef.current.stop(); serviceRef.current = null; }
      if (batcherRef.current) { batcherRef.current.stopAndFlush(); batcherRef.current = null; }
    };
  }, [shouldRun, agentLang, agentMuted]);

  useEffect(() => {
    if (!customerShouldRun) return undefined;
    customerCallSidRef.current = resolveCallSid();
    customerBatcherRef.current = createTranscriptBatcher({
      post: (payload) => postApiCall(API_CALL_TRANSCRIPTS, payload),
      getMeta: () => ({
        callSid: customerCallSidRef.current || resolveCallSid(),
        traceId: resolveTraceId(),
        provider: "browser_speech",
        language: customerLang,
      }),
    });
    customerServiceRef.current = createCustomerNativeTranscriptionService({
      lang: customerLang,
      shouldRestart: () => {
        const cs = useCallStore.getState().callState;
        const ts = useTranscriptStore.getState();
        return (
          ts.enabled && isWebrtcProfileActive() &&
          ts.customerLang === customerLang && ACTIVE_CALL_STATES.includes(cs)
        );
      },
      isAudioTrackSupported: isSpeechRecognitionAudioTrackSupported,
      onAudioTrackUnsupported: () => {
        blockCustomerAudioTrack();
        useTranscriptStore.getState().setCustomerSupported(false);
      },
      onInterim: (t) => useTranscriptStore.getState().setCustomerInterim(t),
      onFinal: (t) => {
        useTranscriptStore.getState().addFinalLine(t, { speaker: "Customer", source: "remote" });
        customerBatcherRef.current?.add({
          speaker: "Customer", source: "remote", text: t,
          timestamp: new Date().toISOString(), isFinal: true,
        });
      },
      onError: (e) => useTranscriptStore.getState().setError(e),
      onStatus: (st) => useTranscriptStore.getState().setCustomerStatus(st),
    });
    customerServiceRef.current.start();
    return () => {
      if (customerServiceRef.current) { customerServiceRef.current.stop(); customerServiceRef.current = null; }
      if (customerBatcherRef.current) { customerBatcherRef.current.stopAndFlush(); customerBatcherRef.current = null; }
    };
  }, [customerShouldRun, customerLang]);

  useEffect(() => {
    customerServiceRef.current?.setPaused?.(callState === "hold");
  }, [callState, customerShouldRun]);

  useEffect(() => {
    if (callState === "idle") useTranscriptStore.getState().reset();
  }, [callState]);
}
